import React from "react";
import { CircleMarker, Marker, Polyline, Popup, Tooltip } from "react-leaflet";
import * as turf from "@turf/turf";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import polyline from "@mapbox/polyline";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { apiKey, apiPrefix, positionAccuracy, zoomRange } from "../../config";
import {
  addMinutesToDate,
  dateToString,
  dateToString2,
  dateToTimeString,
  deviationCalculation,
  getBusFillingColor,
  getTimeOnly,
  isNumeric,
} from "../../utils/helpers";
import { setRouteMapRange } from "../../redux/actions";
import { positionSelectedIcon } from "../../utils/mapIcons";
import { AgencyContext } from "../../hooks/AgencyContext";
import { GetOccupancyStatus } from "../helpers/OccupancyStatusIcon";
import { InfoPopup } from "../helpers/PolylinesComponents";
// import { getWheelColors } from "../../utils/helperWheelColors";

//* The main component
const SingleTripReportMap = ({ showLineNumbers }: { showLineNumbers: boolean }): JSX.Element => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const dispatch: Dispatch = useAppDispatch();
  const [tripsDataFromRedux, routesDetailsFromRedux, mapView]: [
    {
      vehicleId: string;
      tripReport: TripReport[];
      selectedDay: Date;
      tripToFetch: {
        value: string;
        label: string;
      };
      tripInfo: {
        startTime: string;
        endTime: string;
        headsign: string;
        routeId: string;
        routeShortName: string;
        encodedTripPolyline: string;
      };
    },
    RouteDetails[],
    { lat: number; lon: number; zoom: number },
  ] = useAppSelector((state: RootState) => [state?.tripsReportData, state?.routesInfo?.routesInfo, state?.mapView?.mapView]);
  // console.log("tripsDataFromRedux:", tripsDataFromRedux);

  //* AgencyContext
  const { agencyId, initialMapRange } = React.useContext(AgencyContext) as AgencyContextType;

  const [tripsData, setTripsData] = React.useState<TripReport[] | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [currentTrip, setCurrentTrip] = React.useState<string>("");
  const [timeRange, setTimeRange] = React.useState<{ minDate: null | Date; maxDate: null | Date }>({
    minDate: null,
    maxDate: null,
  });
  const [routeId, setRouteId] = React.useState<string>("");
  const [positionsStateArray, setPositionsStateArray] = React.useState<Array<VehicleReportAVL> | null>(null);
  const [routeDetails, setRouteDetails] = React.useState<RouteDetails | null>(null);
  // console.log("routeDetails: ", routeDetails);

  //* Init - Dispatch initial map range
  React.useEffect(() => {
    if (!initialMapRange) return;
    setTimeout(() => {
      dispatch(setRouteMapRange(initialMapRange as LanLngPoint[]));
    }, 100);
  }, [dispatch, initialMapRange]);

  React.useEffect(() => {
    if (tripsDataFromRedux && tripsDataFromRedux?.tripReport && routesDetailsFromRedux) {
      setSelectedDate(tripsDataFromRedux?.selectedDay);
      setCurrentTrip(tripsDataFromRedux?.tripToFetch?.value);
      // setTripsData(tripsDataFromRedux?.tripReport);

      const tripInfoFromRedux = tripsDataFromRedux?.tripInfo;
      //  console.log({ tripInfoFromRedux });
      const currentRouteId: string = tripInfoFromRedux?.routeId || tripInfoFromRedux?.routeShortName;
      setRouteId(currentRouteId);

      const currentRouteInfo = routesDetailsFromRedux?.find((route: RouteDetails) => route?.id === routeId) as RouteDetails;
      // console.log("currentRouteInfo:", currentRouteInfo);
      setRouteDetails(currentRouteInfo);

      const tripReportDataToSet: TripReport[] = [...tripsDataFromRedux?.tripReport];

      for (let i = 0; i < tripReportDataToSet?.length; i++) {
        tripReportDataToSet[i].routeInfo = currentRouteInfo;
      }
      setTripsData(tripReportDataToSet);

      const arrivalTimes: string[] = tripsDataFromRedux?.tripReport?.map((elem: TripReport) => elem.arrivaltime);
      const departureTimes: string[] = tripsDataFromRedux?.tripReport?.map((elem: TripReport) => elem.departuretime);
      // console.log({ arrivalTimes, departureTimes });
      const times: string[] = ([] as string[]).concat(arrivalTimes, departureTimes);
      // console.log({ times });
      const timesFiltered: string[] = times.filter((elem: string) => elem !== undefined);
      // console.log({ timesFiltered });
      const maxDateTemp =
        times.length >= 1 && (new Date(Math.max(...timesFiltered.map((time: string) => new Date(time) as any))) as Date);
      const minDateTemp =
        times.length >= 1 && (new Date(Math.min(...timesFiltered.map((time: string) => new Date(time) as any))) as Date);
      // console.log({ maxDateTemp, minDateTemp });

      const maxDate = maxDateTemp && addMinutesToDate(maxDateTemp, 1);
      const minDate = minDateTemp && addMinutesToDate(minDateTemp, -1);
      // console.log({ maxDate, minDate });
      minDate && maxDate && setTimeRange({ maxDate, minDate });
    }
  }, [routeId, routesDetailsFromRedux, tripsDataFromRedux]);

  React.useEffect(() => {
    const tripReportData = tripsDataFromRedux?.tripReport;
    setTimeout(() => {
      if (tripReportData && tripReportData.length >= 1) {
        const multiPointArray = [] as [number, number][];
        for (let i = 0; i < tripReportData.length; i++) {
          multiPointArray.push([tripReportData[i].lat, tripReportData[i].lon]);
        }
        // console.log("multiPointArray:", multiPointArray);
        const multiPoint = turf.multiPoint(multiPointArray);
        // Map range
        const bbox = turf.bbox(multiPoint);
        // console.log({ bbox });
        const routeMapRangeArray = [
          { lat: bbox[0] - zoomRange, lng: bbox[1] - zoomRange },
          { lat: bbox[2] + zoomRange, lng: bbox[3] + zoomRange },
        ];
        // console.log("routeMapRangeArray:", routeMapRangeArray);
        dispatch(setRouteMapRange(routeMapRangeArray));
      }
    }, 100);
  }, [dispatch, tripsDataFromRedux]);

  //* Fetching buses info for AVL Report
  React.useEffect(() => {
    const getReportData = async () => {
      if (tripsDataFromRedux && selectedDate && timeRange?.minDate && timeRange?.maxDate) {
        // Numbers of days is set to 1: numDays = "1"
        const numDays = "1";
        const busIdToFetch = tripsDataFromRedux?.vehicleId[0];
        // console.log({ busIdToFetch });

        const fetchDate = dateToString(selectedDate!);
        // console.log({ fetchDate });
        // console.log("timeRange?.minDate:", timeRange?.minDate);
        // console.log("timeRange?.maxDate:", timeRange?.maxDate);

        const tripInfoFromRedux = tripsDataFromRedux?.tripInfo;
        const maxTimeFromTripInfo = tripInfoFromRedux?.endTime;
        const minTimeFromTripInfo = tripInfoFromRedux?.startTime;
        // console.log({ maxTimeFromTripInfo, minTimeFromTripInfo });

        const minTimeFromRange = dateToTimeString(timeRange?.minDate);
        const maxTimeFromRange = dateToTimeString(timeRange?.maxDate);
        // console.log({ minTimeFromRange, maxTimeFromRange });

        const minTimeFinal = minTimeFromRange < minTimeFromTripInfo ? minTimeFromRange : minTimeFromTripInfo;
        const maxTimeFinal = maxTimeFromRange > maxTimeFromTripInfo ? maxTimeFromRange : maxTimeFromTripInfo;
        // console.log({ minTimeFinal, maxTimeFinal });

        const encodedBeginTime = encodeURIComponent(minTimeFinal);
        const encodedEndTime = encodeURIComponent(maxTimeFinal);
        // console.log({ encodedBeginTime, encodedEndTime });

        const URL = `${apiPrefix}/v1/key/${apiKey}/agency/${agencyId}/reports/avlReport?v=${busIdToFetch}&beginDate=${fetchDate}&numDays=${numDays}&beginTime=${encodedBeginTime}&endTime=${encodedEndTime}`;

        await axios
          .get(URL)
          .then(({ data }) => {
            // console.log("data", data);
            const dataToSet = data?.data;
            // console.log("dataToSet.length:", dataToSet.length);
            dataToSet && dataToSet?.map((elem: VehicleReportAVL) => (elem.lat = Number(elem.lat.toFixed(positionAccuracy))));
            dataToSet && dataToSet?.map((elem: VehicleReportAVL) => (elem.lon = Number(elem.lon.toFixed(positionAccuracy))));

            const dataToSetFiltered = dataToSet?.filter((obj: VehicleReportAVL, number: number) => {
              return (
                dataToSet.findIndex((elem: VehicleReportAVL) => {
                  return elem.lat === obj.lat && elem.lon === obj.lon;
                }) === number
              );
            });
            // console.log("dataToSetFiltered:", dataToSetFiltered, dataToSetFiltered.length);
            setPositionsStateArray(dataToSetFiltered);
          })
          .catch((error) => {
            if (error.response) {
              console.error("error.response.status:", error.response.status);
            }
          });
      }
    };
    getReportData();
  }, [agencyId, selectedDate, timeRange?.maxDate, timeRange?.minDate, tripsDataFromRedux]);

  //* Trip Polyline
  const encodedTripPolyline: string = tripsDataFromRedux?.tripInfo?.encodedTripPolyline || "";

  const TripDataStops = (): JSX.Element => {
    return (
      <React.Fragment>
        {routeId &&
          tripsData &&
          (tripsData.map((elem: TripReport, index: number) => (
            <CircleMarker
              pathOptions={{
                fillColor: deviationCalculation(elem?.departuretime, elem?.scheduledtime).colorDeviationHex || "#000",
                color: deviationCalculation(elem?.departuretime, elem?.scheduledtime).colorDeviationHex || "#000",
                opacity: 1.0,
                fillOpacity: 0.8,
              }}
              key={String(elem.lat + elem.lon + index)}
              center={{
                lat: elem.lat,
                lng: elem.lon,
              }}
              radius={8}
            >
              <Popup>
                <div>
                  {t("Data")}: <span className="span_bold">{selectedDate && dateToString2(selectedDate)}</span>
                  <br />
                  {t("Linia")}:{" "}
                  <span
                    className="span_bold"
                    style={{
                      color: elem?.routeInfo?.textColor,
                      backgroundColor: elem?.routeInfo?.color,
                      padding: "1px 3px",
                      textAlign: "center",
                      minWidth: "32px",
                      display: "inline-block",
                    }}
                  >
                    {elem?.routeInfo?.shortName === routeId
                      ? elem?.routeInfo?.shortName
                      : `${elem?.routeInfo?.shortName} (${routeId})`}
                  </span>
                  <br />
                  {t("Pojazd")}:{" "}
                  <span className="span_bold">
                    {elem?.vehiclename ? `${elem?.vehiclename} (${elem?.vehicleid})` : elem?.vehicleid}
                  </span>
                  <br />
                  {`${t("Kurs")} / ${t("Kierunek")}`}:{" "}
                  <span className="span_bold">
                    {currentTrip} / {elem.directionid}
                  </span>
                  <br />
                  {t("Numer / Ilość")}: <span className="span_bold">{`${elem.stoporder} / ${tripsData.length}`}</span>
                  <br />
                  {t("Nazwa")} <span className="span_bold">{elem.stopname}</span>
                  <br />
                  {t("Id przystanku")}: <span className="span_bold">{elem.stopid}</span>
                  <br />
                  {t("Pozycja")}:{" "}
                  <span className="span_bold">
                    {" "}
                    {elem?.lat?.toFixed(positionAccuracy) || t("b/d")}
                    {", "}
                    {elem?.lon?.toFixed(positionAccuracy) || t("b/d")}
                  </span>
                  <br />
                  {t("Przyjazd")}: <span className="span_bold">{getTimeOnly(elem?.arrivaltime) || t("b/d")}</span>
                  <br />
                  {t("Odjazd")}: <span className="span_bold">{getTimeOnly(elem?.departuretime) || t("b/d")}</span>
                  <br />
                  {t("Wg rozkładu")}: <span className="span_bold">{getTimeOnly(elem?.scheduledtime) || t("b/d")}</span>
                  <br />
                  {t("Odchyłka")}:{" "}
                  <span
                    className="span_bold "
                    style={{
                      backgroundColor: deviationCalculation(elem?.departuretime, elem?.scheduledtime).colorDeviation,
                    }}
                  >
                    {!elem?.difference_in_seconds
                      ? t("b/d")
                      : deviationCalculation(elem?.departuretime, elem?.scheduledtime).stringDeviation}
                  </span>
                  <br />
                  {t("passengerCount")}: <span className="span_bold">{elem?.passenger_count || t("b/d")}</span>
                  <br />
                  {t("Zapełnienie")}:{" "}
                  <span
                    className="span_bold"
                    style={{
                      backgroundColor: getBusFillingColor(elem?.passenger_fullness),
                      padding: "1px 3px",
                      textAlign: "center",
                      minWidth: "32px",
                      display: "inline-block",
                    }}
                  >
                    {isNumeric(elem?.passenger_fullness) ? `${Number(elem?.passenger_fullness).toFixed(0)}%` : t("b/d")}
                  </span>
                  <br />
                  {t("Status zajętości")}:{" "}
                  <span className="span_bold">
                    <GetOccupancyStatus percentage={elem?.passenger_fullness} />
                  </span>
                </div>
              </Popup>
              {showLineNumbers && (
                <Tooltip permanent={true} direction={index % 2 === 0 ? "right" : "left"}>
                  {t("Id")}
                  <span className="span_bold"> {elem.stopid}</span> {`(${elem.stoporder}/${tripsData.length})`}
                </Tooltip>
              )}
              {pathname.includes("trip") && mapView && mapView.lat === elem.lat && mapView.lon === elem.lon ? (
                <Marker
                  icon={positionSelectedIcon}
                  position={{
                    lat: elem.lat,
                    lng: elem.lon,
                  }}
                />
              ) : null}
            </CircleMarker>
          )) as JSX.Element[])}
      </React.Fragment>
    );
  };

  const InfoPositionDiv = ({ elem }: { elem: VehicleReportAVL }): JSX.Element => (
    <div>
      {t("Id pojazdu")}:{" "}
      <span className="span_bold">{elem?.name ? `${elem?.name} (${elem?.vehicleid})` : elem?.vehicleid}</span>
      <br />
      {t("Czas")}: <span className="span_bold">{elem.time}</span>
      <br />
      {t("Pozycja")}:{" "}
      <span className="span_bold">
        {elem.lat}, {elem.lon}
      </span>
      <br />
      {t("Źródło")}: <span className="span_bold">{elem.source}</span>
      <br />
    </div>
  );

  const circleOptions = {
    color: "Blue",
    // color: getWheelColors(routeDetails?.color || "", 1)?.[0],
    weight: 2,
    // fillColor: getWheelColors(routeDetails?.color || "", 1)?.[0],
    fillColor: "Blue",
    fillOpacity: 1,
    opacity: 1,
  };

  const historicalPositionsPath =
    positionsStateArray &&
    (positionsStateArray.map((elem: VehicleReportAVL, index: number) => (
      <CircleMarker
        key={String(elem.lat + elem.lon + index)}
        center={{
          lat: elem.lat,
          lng: elem.lon,
        }}
        radius={4}
        pathOptions={circleOptions}
      >
        <Popup>
          <InfoPositionDiv elem={elem} />
        </Popup>
      </CircleMarker>
    )) as JSX.Element[]);

  return (
    <React.Fragment>
      {encodedTripPolyline && routeDetails && currentTrip ? (
        <TripPolyline encodedTripPolyline={encodedTripPolyline} routeDetails={routeDetails} currentTrip={currentTrip} />
      ) : null}
      {tripsData && TripDataStops()}
      {positionsStateArray && routeDetails && historicalPositionsPath}
    </React.Fragment>
  );
};

export default SingleTripReportMap;

//* TripPolyline
const TripPolyline = ({
  encodedTripPolyline,
  routeDetails,
  currentTrip,
}: {
  encodedTripPolyline: string;
  routeDetails: RouteDetails;
  currentTrip: string;
}): JSX.Element => {
  const { t } = useTranslation();

  const positions: [number, number][] = polyline.decode(encodedTripPolyline, 6);
  const pathOptions = { opacity: 1, color: routeDetails?.color, weight: 4 };

  return (
    <React.Fragment>
      <Polyline
        pathOptions={pathOptions}
        positions={positions}
        children={
          <Popup>
            <InfoPopup $width={"auto"}>
              <p>
                {t("Kurs")}: <span>{currentTrip || t("b/d")}</span>
              </p>
            </InfoPopup>
          </Popup>
        }
      />
    </React.Fragment>
  );
};
